import { useMemo } from 'react'
import { RowStyle } from '../../../../interfaces/grids/RowStyle'
import {
  CellClassParams,
  ICellRendererParams,
  KeyCreatorParams,
  ValueFormatterParams,
  ValueParserParams,
} from 'ag-grid-community'
import { numberFormatter } from '../../../../utils/numberFormatter'
import { getRowStyle } from '../../../../utils/getRowStyle'
import { numericValueParser } from '../helper/numericValueParser'
import CommentCellRenderer from '../cellRenderers/CommentCellRenderer/CommentCellRenderer'
import useCheckIfSheetEditable from '../customHooks/useCheckIfSheetEditable'
import { AggTranslations } from '../../../../interfaces/grids/performance/AggTranslation'
import TotalAggCellRenderer from '../cellRenderers/TotalAggCellRenderer'
import { useProjektprognosEditingContext } from '../../context/ProjektprognosEditingContext'
import { useCommentAccess } from '../../customHooks/useCommentAccess'
import { ReadOnlyLargeTextCellEditor } from '../cellEditors/ReadOnlyLargeTextCellEditor'

interface PerformanceColDefs {
  lockedBudget: boolean
  performanceTotals: AggTranslations | undefined
  selectedSnapshotName?: string
}

export const usePerformanceColDefs = ({
  lockedBudget,
  performanceTotals,
  selectedSnapshotName,
}: PerformanceColDefs) => {
  const checkIfEditable = useCheckIfSheetEditable()
  const { easterEggMode, selectedPeriodReport } =
    useProjektprognosEditingContext()
  const { canOpenCommentEditor, canEditComment } = useCommentAccess({
    periodReport: selectedPeriodReport,
  })

  const performanceColDefs: object[] = useMemo(() => {
    const rowStyle: RowStyle = { backgroundColor: '#d9dcde', fontWeight: '' }

    return [
      {
        field: 'grandParentDisplayName',
        hide: true,
        rowGroup: true,
        keyCreator: (params: KeyCreatorParams) => {
          return (
            params.node?.data.grandParentDisplayName ??
            params.node?.data.displayName
          )
        },
      },
      {
        field: 'parentDisplayName',
        hide: true,
        rowGroup: true,
      },
      {
        headerName: !lockedBudget ? 'Budget' : 'Budget*',
        field: 'budget',
        type: 'numericColumn',
        cellRenderer: (params: ICellRendererParams) =>
          TotalAggCellRenderer(params, easterEggMode),
        cellRendererParams: {
          aggTranslations: performanceTotals,
        },
        valueFormatter: (params: ValueFormatterParams) =>
          numberFormatter(params, easterEggMode),
        cellStyle: (params: CellClassParams) =>
          getRowStyle(params, { ...rowStyle }),
        editable: !lockedBudget && checkIfEditable,
        valueParser: (params: ValueParserParams) => numericValueParser(params),
      },
      {
        headerName: 'Utfall',
        field: 'totalOutcome',
        type: 'numericColumn',
        cellRenderer: (params: ICellRendererParams) =>
          TotalAggCellRenderer(params, easterEggMode),
        cellRendererParams: {
          aggTranslations: performanceTotals,
        },
        valueFormatter: (params: ValueFormatterParams) =>
          numberFormatter(params, easterEggMode),
        cellStyle: (params: CellClassParams) =>
          getRowStyle(params, { ...rowStyle }),
      },
      {
        headerName: 'Föreg. prognos',
        field: 'previousTotalPrognosis',
        type: 'numericColumn',
        cellRenderer: (params: ICellRendererParams) =>
          TotalAggCellRenderer(params, easterEggMode),
        cellRendererParams: {
          aggTranslations: performanceTotals,
        },
        valueFormatter: (params: ValueFormatterParams) =>
          numberFormatter(params, easterEggMode),
        cellStyle: (params: CellClassParams) =>
          getRowStyle(params, { ...rowStyle }),
        headerTooltip: 'Föreg. prognos',
      },
      {
        headerName: 'Prognos',
        field: 'totalPrognosis',
        type: 'numericColumn',
        cellRenderer: (params: ICellRendererParams) =>
          TotalAggCellRenderer(params, easterEggMode),
        cellRendererParams: {
          aggTranslations: performanceTotals,
        },
        valueFormatter: (params: ValueFormatterParams) =>
          numberFormatter(params, easterEggMode),
        cellStyle: (params: CellClassParams) =>
          getRowStyle(params, { ...rowStyle }),
        editable: checkIfEditable,
        valueParser: (params: ValueParserParams) => numericValueParser(params),
      },
      {
        headerName: 'Diff',
        headerTooltip: 'Prognos - Föreg. prognos',
        field: 'difference',
        type: 'numericColumn',
        cellRenderer: (params: ICellRendererParams) =>
          TotalAggCellRenderer(params, easterEggMode),
        cellRendererParams: {
          aggTranslations: performanceTotals,
        },
        valueFormatter: (params: ValueFormatterParams) =>
          numberFormatter(params, easterEggMode),
        cellStyle: (params: CellClassParams) =>
          getRowStyle(params, { ...rowStyle }),
      },
      {
        headerName: 'Kommentar',
        field: 'comment',
        cellRenderer: CommentCellRenderer,
        type: 'rightAligned',
        cellStyle: (params: CellClassParams) =>
          getRowStyle(params, { ...rowStyle }),
        editable: canOpenCommentEditor,
        singleClickEdit: true,
        cellEditor: ReadOnlyLargeTextCellEditor,
        cellEditorPopup: true,
        cellEditorParams: {
          maxLength: 500,
          rows: 10,
          cols: 30,
          readOnly: !canEditComment,
          readOnlyMessage: 'Du har inte behörighet att redigera kommentaren',
        },
      },
    ]
  }, [
    lockedBudget,
    performanceTotals,
    checkIfEditable,
    canOpenCommentEditor,
    canEditComment,
    easterEggMode,
  ])

  return performanceColDefs
}
